import { ReactNode } from 'react';
import { Zap, Home, Sun, TrendingUp } from 'lucide-react';
import { GetPlantOverviewResponse } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-overview.ts';
import style from './plant-production-and-storage-today-metrics.module.scss';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { useTranslation } from 'react-i18next';
import { formatMetricValue } from '@/utils/format-metric-value.ts';
import { cn } from '@/utils/utils.ts';

const MetricCard = ({
  label,
  value,
  unit,
  icon,
  isLoading,
}: {
  label: string;
  value: string;
  unit: string;
  icon: ReactNode;
  isLoading: boolean;
}) => (
  <DashboardCard className="h-[90px] min-w-[220px]" isLoading={isLoading}>
    <div className="flex justify-between">
      <div className="flex flex-col gap-3 px-2">
        <span className="text-muted-foreground text-sm text-nowrap">{label}</span>
        <div className="flex items-end gap-2">
          <span className="text-2xl font-semibold">
            {value} <span className="text-muted-foreground text-xl">{unit}</span>
          </span>
        </div>
      </div>
      <div className="self-center">
        <div className="flex size-14 items-center justify-center">{icon}</div>
      </div>
    </div>
  </DashboardCard>
);

export const PlantProductionAndStorageTodayMetrics = ({
  overview,
  isLoading,
  className,
}: {
  overview: GetPlantOverviewResponse | undefined | null;
  isLoading: boolean;
  className?: string;
}) => {
  const { t } = useTranslation(['translation'], { keyPrefix: 'metrics' });

  const ConsumptionToday = formatMetricValue(overview?.consumptionToday);
  const SelfConsumption = formatMetricValue(overview?.selfConsumptionToday);
  const YieldToday = formatMetricValue(overview?.yieldToday);
  const TotalYield = formatMetricValue(overview?.totalYield);

  return (
    <div className={cn(style.today_metrics, className)}>
      <div className={`${style.today_metrics__cards}`}>
        <MetricCard
          value={ConsumptionToday.value}
          unit={ConsumptionToday.unit}
          icon={<Zap className="text-gray-500" size={32} />}
          isLoading={isLoading}
          label={t('today_consumption')}
        />
        <MetricCard
          value={SelfConsumption.value}
          unit={SelfConsumption.unit}
          icon={<Home className="text-gray-500" size={32} />}
          isLoading={isLoading}
          label={t('today_self_consumption')}
        />
        <MetricCard
          value={YieldToday.value}
          unit={YieldToday.unit}
          icon={<Sun className="text-gray-500" size={32} />}
          isLoading={isLoading}
          label={t('today_production')}
        />
        <MetricCard
          value={TotalYield.value}
          unit={TotalYield.unit}
          icon={<TrendingUp className="text-gray-500" size={32} />}
          isLoading={isLoading}
          label={t('total_production')}
        />
      </div>
    </div>
  );
};
